import PropTypes from "prop-types";
import { Box, Stack, Typography, Avatar } from "@mui/material";
import {
  User,
  Crown,
  Shield,
  HeadphonesIcon,
  LogOut,
  ChevronsUpDown,
  CreditCard,
  Settings,
  Globe,
  Video,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./DropdownMenu.jsx";
import { Badge } from "./Badge.jsx";
import { useAuth } from "./contexts/AuthContext.jsx";
import { useTheme } from "../theme/useTheme";
import { FEATURE_SERSEO_CONTENT, hasAgencyFeature } from "@/utils/agencyFeatures";
import { getSettingsSections } from "@/Pages/Settings/settingsSections";
import { SETTINGS_BASE_PATH } from "@/Pages/Settings/settingsPaths";

const roleConfig = {
  superadmin: {
    label: "Super Administrador",
    shortLabel: "Super Admin",
    Icon: Crown,
  },
  agency_admin: {
    label: "Administrador",
    shortLabel: "Admin",
    Icon: Shield,
  },
  consultant: {
    label: "Consultor",
    shortLabel: "Consultor",
    Icon: HeadphonesIcon,
  },
  freelance_ticket: {
    label: "Freelance Tickets",
    shortLabel: "Freelance",
    Icon: HeadphonesIcon,
  },
  client: {
    label: "Cliente",
    shortLabel: "Cliente",
    Icon: User,
  },
};

const showPortalRoles = ["superadmin", "agency_admin", "consultant"];

export function UserBox({ profileImage, compact = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, colors, neutrals, getRoleBranding } = useTheme();

  if (!user) return null;

  const branding = getRoleBranding(user.role);
  const primaryColor = branding.primary || theme.palette.primary.main;
  const contrastText =
    branding.contrastText || theme.palette.primary.contrastText;

  const { Icon, shortLabel } = roleConfig[user.role] ?? roleConfig.client;

  const initials = (user.name || "")
    .split(" ")
    .map((n) => n[0])
    .join("");

  const planSection = getSettingsSections(user).find((section) => section.id === "plan");
  const subtitle = [user.agencyName, shortLabel].filter(Boolean).join(" · ");

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const showPortal =
    showPortalRoles.includes(user.role) && hasAgencyFeature(user, FEATURE_SERSEO_CONTENT);

  const avatar = (
    <Box sx={{ position: "relative", flexShrink: 0, display: "flex" }}>
      <Avatar
        src={profileImage}
        sx={{
          width: compact ? 36 : 32,
          height: compact ? 36 : 32,
          bgcolor: primaryColor,
          color: contrastText,
          fontSize: "0.8125rem",
          fontWeight: 600,
        }}
      >
        {initials}
      </Avatar>
      {planSection?.badge && (
        <Box
          component="span"
          aria-label="Pago pendiente"
          sx={{
            position: "absolute",
            top: -2,
            right: -2,
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: colors.brand.pink,
            border: `2px solid ${theme.palette.background.paper}`,
          }}
        />
      )}
    </Box>
  );

  const menuContent = (
    <DropdownMenuContent
      sideOffset={8}
      sx={{ width: 224 }}
      // Comprimido: a la derecha del avatar; si no, encima de la tarjeta
      {...(compact
        ? {
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
          transformOrigin: { vertical: "bottom", horizontal: "left" },
        }
        : {
          anchorOrigin: { vertical: "top", horizontal: "left" },
          transformOrigin: { vertical: "bottom", horizontal: "left" },
        })}
    >
      <DropdownMenuLabel sx={{ fontWeight: 400 }}>
        <Stack spacing={0.25}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.2 }}>
            {user.name}
          </Typography>
          {user.email && (
            <Typography noWrap sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
              {user.email}
            </Typography>
          )}
        </Stack>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {showPortal && (
        <>
          <DropdownMenuItem onClick={() => navigate("/Portal/ZonaSerseo")}>
            <Globe size={16} style={{ marginRight: 8 }} />
            <span>Zona Serseo</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate("/Portal/Academia")}>
            <Video size={16} style={{ marginRight: 8 }} />
            <span>Academia</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem onClick={() => navigate(SETTINGS_BASE_PATH)}>
        <Settings size={16} style={{ marginRight: 8 }} />
        <span>Ajustes</span>
      </DropdownMenuItem>
      {planSection && (
        <DropdownMenuItem onClick={() => navigate(planSection.path)}>
          <CreditCard size={16} style={{ marginRight: 8 }} />
          <span style={{ flex: 1 }}>{planSection.label}</span>
          {planSection.badge && <Badge variant="destructive">{planSection.badge}</Badge>}
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={handleLogout}>
        <LogOut size={16} style={{ marginRight: 8 }} />
        <span>Cerrar Sesión</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  const triggerSx = {
    all: "unset",
    boxSizing: "border-box",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    width: "100%",
    borderRadius: "10px",
    transition: "background-color 150ms",
    "&:hover": { backgroundColor: neutrals.gray[100] },
    "&:focus-visible": { outline: `2px solid ${primaryColor}`, outlineOffset: 2 },
  };

  // Vista comprimida del sidebar: solo el avatar, que abre el mismo menú
  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Box
            component="button"
            type="button"
            aria-label="Abrir menú de usuario"
            sx={{ ...triggerSx, justifyContent: "center", p: 0.5 }}
          >
            {avatar}
          </Box>
        </DropdownMenuTrigger>
        {menuContent}
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Box
          component="button"
          type="button"
          aria-label="Abrir menú de usuario"
          sx={{ ...triggerSx, gap: 1.25, p: 1 }}
        >
          {avatar}
          <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3, color: theme.palette.text.primary }}>
              {user.name}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              {!user.agencyName && <Icon size={12} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />}
              <Typography noWrap sx={{ fontSize: "0.75rem", lineHeight: 1.3, color: theme.palette.text.secondary }}>
                {subtitle}
              </Typography>
            </Stack>
          </Box>
          <ChevronsUpDown size={16} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
        </Box>
      </DropdownMenuTrigger>
      {menuContent}
    </DropdownMenu>
  );
}

UserBox.propTypes = {
  profileImage: PropTypes.string,
  compact: PropTypes.bool,
};

export default UserBox;
